import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { ConfigContext } from './ConfigContext';
import { constants } from '../constants';
import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, Divider, FormControl, IconButton, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
// Helper function to validate resource actions from form state
const getValidResourceActionsFromForm = (resourceActions) => {
    return resourceActions
        .filter((ra) => ra.resource.trim() && ra.actions.length > 0)
        .map((ra) => ({
        action: ra.actions,
        applicationresource_id: [ra.resource]
    }));
};
// Helper function to create policy data from JSON
const createPolicyDataFromJson = (parsedJsonData, validResourceActions, jsonString, policy, isEditMode) => {
    // Extract application_id from parsed JSON, handling various formats
    let applicationId = parsedJsonData.application_id ?? policy?.application_id;
    // No unreachable return, just assign and use
    return {
        ...(isEditMode && policy?.id && { id: policy.id }),
        name: parsedJsonData.name.trim(),
        effect: parsedJsonData.effect.toLowerCase(),
        resource_action: validResourceActions,
        application_id: applicationId,
        specification: jsonString,
        dateAdded: policy?.dateAdded ?? new Date().toLocaleDateString()
    };
};
// Helper function to create policy data from form state
const createPolicyDataFromForm = (policyName, effect, validResourceActions, applicationIds, policy, isEditMode) => {
    // Handle 'none' option - convert to empty array to indicate no application
    let applicationId;
    if (applicationIds.includes('none')) {
        applicationId = [];
    }
    else if (applicationIds.length > 0) {
        applicationId = applicationIds;
    }
    else {
        applicationId = policy?.application_id;
    }
    return {
        ...(isEditMode && policy?.id && { id: policy.id }),
        name: policyName.trim(),
        effect: effect.toLowerCase(),
        resource_action: validResourceActions,
        application_id: applicationId,
        specification: JSON.stringify({
            resourceType: 'Policy',
            id: isEditMode ? policy?.id : undefined,
            application_id: applicationId || 1,
            name: policyName.trim(),
            effect: effect.toLowerCase(),
            resource_action: validResourceActions,
            details: {},
            metadata: {
                versionId: 1,
                createdDate: new Date().toISOString(),
                updatedDate: new Date().toISOString(),
                createdBy: '',
                updatedBy: ''
            }
        }, null, 2),
        dateAdded: policy?.dateAdded ?? new Date().toLocaleDateString()
    };
};
// Helper function to normalize effect value
const normalizeEffectValue = (effect) => {
    if (!effect)
        return 'Allow';
    return effect.toLowerCase() === 'allow' ? 'Allow' : 'Deny';
};
// Helper function to get application IDs from policy
const getApplicationIdsFromPolicy = (applicationId) => {
    if (!applicationId)
        return ['none'];
    const appIds = Array.isArray(applicationId)
        ? applicationId
        : [applicationId];
    const hasNoApp = appIds.every((id) => id === null || id === undefined || id === 'null');
    return hasNoApp ? ['none'] : appIds;
};
// Helper function to parse resource actions from policy
const parseResourceActionsFromPolicy = (resourceAction) => {
    const defaultPair = {
        id: '1',
        resource: '',
        actions: []
    };
    if (!resourceAction || !Array.isArray(resourceAction))
        return [defaultPair];
    const pairs = resourceAction.map((ra, index) => ({
        id: (index + 1).toString(),
        resource: ra.applicationresource_id?.[0] ??
            ra.application_resource?.[0] ??
            ra.resource?.[0] ??
            '',
        actions: ra.action ?? []
    }));
    return pairs.length > 0 ? pairs : [defaultPair];
};
// Default resource action pair
const getDefaultResourceActions = () => [
    { id: '1', resource: '', actions: [] }
];
const PolicyModal = ({ open, onClose, onSave, policy, loading, applications = [] }) => {
    const [policyName, setPolicyName] = useState('');
    const [resourceActions, setResourceActions] = useState([
        {
            id: '1',
            resource: '',
            actions: []
        }
    ]);
    const [effect, setEffect] = useState('Allow');
    const [applicationIds, setApplicationIds] = useState([]);
    const [error, setError] = useState(null);
    const [jsonMode, setJsonMode] = useState('preview');
    const [jsonString, setJsonString] = useState('');
    const [jsonError, setJsonError] = useState(null);
    const { configState } = React.useContext(ConfigContext);
    const isEditMode = !!policy;
    // Function to generate JSON from current form state
    const generateJsonFromForm = () => {
        const validResourceActions = resourceActions.filter((ra) => ra.resource && ra.actions.length > 0);
        // Handle 'none' option - show empty array in JSON preview
        let appIdsForJson = [];
        if (applicationIds.includes('none')) {
            appIdsForJson = [];
        }
        else if (applicationIds.length > 0) {
            appIdsForJson = applicationIds;
        }
        else {
            appIdsForJson = [''];
        }
        return JSON.stringify({
            resourceType: 'Policy',
            id: isEditMode ? policy?.id : undefined,
            application_id: appIdsForJson,
            name: policyName,
            effect: effect.toLowerCase(),
            resource_action: validResourceActions.map((ra) => ({
                action: ra.actions,
                applicationresource_id: [ra.resource]
            })),
            details: {},
            metadata: {
                versionId: 1,
                createdDate: new Date().toISOString(),
                updatedDate: new Date().toISOString(),
                createdBy: '',
                updatedBy: ''
            }
        }, null, 2);
    };
    // Function to sync form from JSON
    const syncFormFromJson = (jsonText) => {
        try {
            const parsed = JSON.parse(jsonText);
            // Update form fields
            if (parsed.name)
                setPolicyName(parsed.name);
            if (parsed.effect) {
                // Handle both lowercase and title case values
                const effectValue = parsed.effect.toLowerCase();
                let newEffect = '';
                if (effectValue === 'allow') {
                    newEffect = 'Allow';
                }
                else if (effectValue === 'deny') {
                    newEffect = 'Deny';
                }
                setEffect(newEffect);
            }
            // Update application_id
            if (parsed.application_id !== undefined) {
                const appIds = Array.isArray(parsed.application_id)
                    ? parsed.application_id
                    : [parsed.application_id];
                setApplicationIds(appIds);
            }
            // Update resource actions
            if (parsed.resource_action &&
                Array.isArray(parsed.resource_action)) {
                const newResourceActions = parsed.resource_action.map((ra, index) => ({
                    id: (index + 1).toString(),
                    resource: ra.applicationresource_id?.[0] ??
                        ra.resource?.[0] ??
                        '',
                    actions: ra.action ?? []
                }));
                setResourceActions(newResourceActions.length > 0
                    ? newResourceActions
                    : [{ id: '1', resource: '', actions: [] }]);
            }
            setJsonError(null);
            return true;
        }
        catch {
            setJsonError(constants.configuration_modal_error_json_invalid);
            return false;
        }
    };
    // Handle JSON mode toggle
    const handleJsonModeToggle = () => {
        if (jsonMode === 'preview') {
            // Switching to edit mode - populate with current form state
            setJsonString(generateJsonFromForm());
            setJsonMode('edit');
        }
        else if (syncFormFromJson(jsonString)) {
            // Switching to preview mode - sync form with JSON successful
            setJsonMode('preview');
        }
        // If sync failed, stay in edit mode to fix errors
    };
    // Handle JSON text change
    const handleJsonChange = (newJsonText) => {
        setJsonString(newJsonText);
        setJsonError(null);
    };
    // Handle application selection change
    const handleAppChange = (event) => {
        const value = event.target.value;
        const newValue = typeof value === 'string' ? value.split(',') : value;
        // If 'none' is selected, clear other selections; if other is selected, remove 'none'
        if (newValue.includes('none') &&
            !applicationIds.map(String).includes('none')) {
            setApplicationIds(['none']);
        }
        else if (newValue.includes('none') && newValue.length > 1) {
            setApplicationIds(newValue.filter((id) => id !== 'none'));
        }
        else {
            setApplicationIds(newValue);
        }
    };
    useEffect(() => {
        if (policy) {
            setPolicyName(policy.name || '');
            setEffect(normalizeEffectValue(policy.effect));
            setApplicationIds(getApplicationIdsFromPolicy(policy.application_id));
            setResourceActions(parseResourceActionsFromPolicy(policy.resource_action));
        }
        else {
            // Reset for new policy
            setPolicyName('');
            setEffect('Allow');
            setApplicationIds([]);
            setResourceActions(getDefaultResourceActions());
        }
        setError(null);
        setJsonMode('preview');
        setJsonError(null);
        // JSON string will be generated in preview mode
    }, [policy, open]);
    const handleSave = () => {
        let parsedJsonData = null;
        // Validate JSON mode if in edit mode
        if (jsonMode === 'edit') {
            try {
                parsedJsonData = JSON.parse(jsonString);
                if (!syncFormFromJson(jsonString)) {
                    return;
                }
            }
            catch {
                setJsonError(constants.configuration_modal_error_json_invalid);
                return;
            }
        }
        // Validate policy name
        const nameToValidate = parsedJsonData?.name ?? policyName;
        if (!nameToValidate?.trim()) {
            setError(constants.policy_modal_error_name_required);
            return;
        }
        // Get validated resource actions
        const validResourceActions = parsedJsonData?.resource_action
            ? parsedJsonData.resource_action
                .filter((ra) => ra.applicationresource_id?.[0] &&
                ra.action?.length > 0)
                .map((ra) => ({
                action: ra.action,
                applicationresource_id: ra.applicationresource_id
            }))
            : getValidResourceActionsFromForm(resourceActions);
        if (validResourceActions.length === 0) {
            setError(constants.policy_modal_error_resource_action_required);
            return;
        }
        // Create policy data based on mode
        const policyData = parsedJsonData && jsonMode === 'edit'
            ? createPolicyDataFromJson(parsedJsonData, validResourceActions, jsonString, policy, isEditMode)
            : createPolicyDataFromForm(policyName, effect, validResourceActions, applicationIds, policy, isEditMode);
        onSave(policyData);
    };
    // For new policies, require at least one application to be selected
    const canSave = policyName.trim() &&
        !loading &&
        (isEditMode || applicationIds.length > 0);
    const getButtonText = () => {
        if (loading) {
            return constants.saving;
        }
        return isEditMode
            ? constants.configuration_modal_button_update
            : constants.configuration_modal_button_add;
    };
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "lg", fullWidth: true, PaperProps: {
            sx: {
                borderRadius: 2,
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                height: '90vh',
                maxHeight: '700px'
            }
        }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 1.5,
                    borderBottom: '1px solid #ebebeb'
                }, children: [_jsx(Typography, { variant: "h6", sx: {
                            color: '#212121',
                            fontFamily: 'Open Sans',
                            fontWeight: 600,
                            fontSize: '20px'
                        }, children: isEditMode
                            ? constants.policy_modal_title_edit(configState?.settings?.workspace_name ??
                                constants.workspace_default_name)
                            : constants.policy_modal_title_add(configState?.settings?.workspace_name ??
                                constants.workspace_default_name) }), _jsx(IconButton, { onClick: onClose, sx: { color: '#595959' }, children: _jsx(CloseIcon, {}) })] }), _jsxs(Box, { sx: { display: 'flex', flex: 1, overflow: 'hidden' }, children: [_jsx(Box, { sx: {
                            width: '400px',
                            p: 3,
                            borderRight: '1px solid #ebebeb',
                            overflow: 'auto'
                        }, children: _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5
                            }, children: [_jsx(TextField, { fullWidth: true, size: "small", label: constants.policy_modal_field_name, value: policyName, onChange: (e) => setPolicyName(e.target.value), required: true }), _jsxs(FormControl, { fullWidth: true, size: "small", children: [_jsx(InputLabel, { children: constants.policy_mgmt_application_label }), _jsxs(Select, { multiple: true, value: applicationIds.map(String), onChange: handleAppChange, label: constants.policy_mgmt_application_label, renderValue: (selected) => {
                                                if (selected.includes('none')) {
                                                    return constants.global_policies;
                                                }
                                                return applications
                                                    .filter((app) => selected.includes(String(app.id)))
                                                    .map((app) => app.name)
                                                    .join(', ');
                                            }, children: [_jsx(MenuItem, { value: "none", children: constants.global_policies }), applications.map((app) => (_jsx(MenuItem, { value: String(app.id), children: app.name }, app.id)))] })] }), _jsx(Divider, { sx: { borderColor: '#ebebeb' } }), _jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }, children: [_jsx(Typography, { variant: "body2", sx: {
                                                color: '#212121',
                                                fontSize: '14px',
                                                fontFamily: 'Open Sans'
                                            }, children: constants.policy_modal_deny }), _jsx(Switch, { checked: effect === 'Allow', onChange: (e) => setEffect(e.target.checked ? 'Allow' : 'Deny'), size: "small", sx: {
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#26890d',
                                                    '& + .MuiSwitch-track': {
                                                        backgroundColor: '#26890d',
                                                        opacity: 0.3
                                                    }
                                                }
                                            } }), _jsx(Typography, { variant: "body2", sx: {
                                                color: '#212121',
                                                fontSize: '14px',
                                                fontFamily: 'Open Sans'
                                            }, children: constants.policy_modal_allow })] }), error && _jsx(Alert, { severity: "error", children: error })] }) }), _jsxs(Box, { sx: {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 3,
                                    py: 2,
                                    borderBottom: '1px solid #ebebeb'
                                }, children: [_jsx(Typography, { variant: "h6", sx: {
                                            color: '#212121',
                                            fontFamily: 'Open Sans',
                                            fontWeight: 600,
                                            fontSize: '16px'
                                        }, children: jsonMode === 'edit'
                                            ? constants.policy_modal_json_editor_label
                                            : constants.policy_modal_json_preview_label }), _jsx(Button, { variant: jsonMode === 'edit' ? 'contained' : 'outlined', size: "small", onClick: handleJsonModeToggle, sx: {
                                            textTransform: 'none',
                                            borderColor: '#26890d',
                                            color: jsonMode === 'edit' ? '#ffffff' : '#26890d',
                                            backgroundColor: jsonMode === 'edit'
                                                ? '#26890d'
                                                : 'transparent',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            '&:hover': {
                                                borderColor: '#26890d',
                                                backgroundColor: jsonMode === 'edit'
                                                    ? '#1f6b0a'
                                                    : 'rgba(38, 137, 13, 0.04)'
                                            }
                                        }, children: jsonMode === 'edit'
                                            ? constants.policy_modal_switch_to_preview
                                            : constants.policy_modal_edit_json })] }), jsonError && (_jsx(Alert, { severity: "error", sx: { mx: 3, mt: 2 }, children: jsonError })), _jsx(Box, { sx: { flex: 1, display: 'flex', overflow: 'hidden' }, children: jsonMode === 'preview' ? (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                                                width: '32px',
                                                backgroundColor: '#ffffff',
                                                p: 1,
                                                borderRight: '1px solid #f0f0f0',
                                                overflow: 'hidden'
                                            }, children: _jsx(Typography, { variant: "body2", component: "pre", sx: {
                                                    color: '#212121',
                                                    fontSize: '14px',
                                                    fontFamily: 'Open Sans',
                                                    lineHeight: 1.62,
                                                    margin: 0
                                                }, children: Array.from({ length: 30 }, (_, i) => `${i + 1}\n`).join('') }) }), _jsx(Box, { sx: {
                                                flex: 1,
                                                backgroundColor: '#f8f8f8',
                                                p: 2,
                                                overflow: 'auto'
                                            }, children: _jsx(Typography, { variant: "body2", component: "pre", sx: {
                                                    color: '#000000',
                                                    fontSize: '14px',
                                                    fontFamily: 'Open Sans',
                                                    lineHeight: 1.62,
                                                    margin: 0,
                                                    whiteSpace: 'pre-wrap'
                                                }, children: (() => {
                                                    // Only show JSON if policy name is provided or it's edit mode
                                                    if (!policyName.trim() &&
                                                        !isEditMode) {
                                                        return '';
                                                    }
                                                    return generateJsonFromForm();
                                                })() }) })] })) : (_jsxs(Box, { sx: {
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }, children: [_jsxs(Box, { sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                px: 2,
                                                py: 1,
                                                backgroundColor: '#f5f5f5',
                                                borderBottom: '1px solid #e0e0e0'
                                            }, children: [_jsx(Typography, { variant: "body2", sx: { color: '#666', fontSize: '12px' }, children: constants.policy_modal_json_editor_tip }), _jsx(Button, { size: "small", onClick: () => setJsonString(generateJsonFromForm()), sx: {
                                                        textTransform: 'none',
                                                        color: '#26890d',
                                                        fontSize: '12px'
                                                    }, children: constants.policy_modal_reset_from_form })] }), _jsx(TextField, { multiline: true, fullWidth: true, value: jsonString, onChange: (e) => handleJsonChange(e.target.value), placeholder: constants.policy_modal_json_placeholder, variant: "outlined", sx: {
                                                flex: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    height: '100%',
                                                    alignItems: 'flex-start',
                                                    fontFamily: 'monospace',
                                                    fontSize: '14px',
                                                    lineHeight: 1.62,
                                                    borderRadius: 0,
                                                    border: 'none',
                                                    '& fieldset': {
                                                        border: 'none'
                                                    }
                                                },
                                                '& .MuiInputBase-input': {
                                                    height: '100% !important',
                                                    overflow: 'auto !important',
                                                    resize: 'none',
                                                    padding: '16px'
                                                }
                                            } })] })) })] })] }), _jsxs(DialogActions, { sx: {
                    p: '16px 24px',
                    gap: 1,
                    borderTop: '1px solid #ebebeb'
                }, children: [_jsx(Button, { onClick: onClose, variant: "outlined", sx: {
                            textTransform: 'none',
                            borderColor: '#d9d9d9',
                            color: '#26890d',
                            fontSize: '16px',
                            fontWeight: 600,
                            fontFamily: 'Open Sans',
                            '&:hover': {
                                borderColor: '#26890d',
                                backgroundColor: 'transparent'
                            }
                        }, children: constants.cancel }), _jsx(Button, { onClick: handleSave, variant: "contained", disabled: !canSave, sx: {
                            textTransform: 'none',
                            backgroundColor: '#26890d',
                            color: '#ffffff',
                            fontSize: '16px',
                            fontWeight: 600,
                            fontFamily: 'Open Sans',
                            '&:hover': {
                                backgroundColor: '#1f6b0a'
                            },
                            '&:disabled': {
                                backgroundColor: '#d9d9d9',
                                color: '#999'
                            }
                        }, children: loading ? (_jsx(CircularProgress, { size: 20, color: "inherit" })) : (getButtonText()) })] })] }));
};
export default PolicyModal;
